import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, EASE } from "@/components/motion/Reveal";
import { useFaq } from "@/hooks/useData";

export function FAQ({ category, items, title = "Häufige Fragen.", label = "FAQ", dark = false }) {
  const { data = [] } = useFaq(category);
  const list = items || data;
  const [open, setOpen] = useState(0);
  const t = dark ? "text-paper" : "text-ink";
  const m = dark ? "text-paper/55" : "text-mute";
  const b = dark ? "border-paper/15" : "border-line";
  if (!list.length) return null;
  return (
    <section className={`${dark ? "bg-ink" : "bg-paper"} py-28 md:py-40 border-t ${b}`} aria-labelledby="faq-title">
      <div className="container-x grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <p className={`label ${m} mb-6 flex items-center gap-4`}><span className={`w-6 h-px ${dark ? "bg-paper/40" : "bg-ink/40"}`} /> {label}</p>
          <h2 id="faq-title" className={`display text-4xl sm:text-5xl lg:text-6xl ${t}`}>{title}</h2>
        </Reveal>
        <div className="lg:col-span-7 lg:col-start-6">
          <ul className={`border-t ${b}`}>
            {list.map((f, i) => {
              const isOpen = open === i;
              return (
                <li key={f.question} className={`border-b ${b}`}>
                  <button
                    type="button"
                    data-testid={`faq-item-${i + 1}`}
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="w-full text-left flex items-start justify-between gap-6 py-6 md:py-7 group"
                  >
                    <span className="flex gap-5">
                      <span className={`label pt-1.5 ${m}`}>{String(i + 1).padStart(2, "0")}</span>
                      <span className={`display text-xl md:text-2xl ${t}`}>{f.question}</span>
                    </span>
                    <Plus size={18} strokeWidth={1.25} className={`shrink-0 mt-1 transition-transform duration-500 ease-out ${isOpen ? "rotate-45" : ""} ${t}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.6, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <p className={`text-sm md:text-base ${m} leading-relaxed pb-7 pl-[3.1rem] max-w-xl`}>{f.answer}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
